import { useSyncExternalStore } from 'react';

/**
 * The shop's theme, resolved per person and then per company. On the website a theme is the
 * accent; the phone also takes a top-band colour from it (mobile `theme/tokens.ts`).
 *
 * <p>
 * <strong>Only the accent is a choice.</strong> The neutrals, slate as the pressable colour, and
 * the twelve queue hues in `components/hues.ts` are fixed. Three tokens move —
 * `--color-primary`, its hover and its wash — so no reading contrast in the product changes
 * whichever a shop picks, which is what makes this a setting rather than a rebuild.
 * </p>
 *
 * <p>
 * <strong>Three answers, in this order</strong>: what this person chose on this device, what their
 * company chose, and the built-in default. The shape is `shell/textScale.ts` — an external store
 * rather than context, because the values are read by `AppProviders` and by a CSS attribute on the
 * root element, neither of which is a component tree worth threading a provider through. Read that
 * file before changing the mechanics here; the reasoning is the same and is written out there.
 * </p>
 *
 * <p>
 * <strong>The hex values are not here.</strong> They are in `style.css` under `[data-accent]`, and
 * this module only writes the attribute — one name, not three colours, so the palette stays in the
 * one file the project says restyling happens in.
 * </p>
 */
export type { Accent } from '@core/vocabulary';
import type { Accent } from '@core/vocabulary';

/** What a shop that never opens the settings screen gets — the owner's choice, 2026-10-05. */
export const defaultAccent: Accent = 'peacock';

/**
 * The four, with the words a person chooses between.
 *
 * <p>
 * No hint says "the default". Both pickers render an option as `label — hint` and already offer
 * "Company default — Peacock" as the empty choice above them, so saying it again produced two em
 * dashes in one line and the word repeated.
 * </p>
 */
export const accentOptions: readonly { value: Accent; label: string; hint: string }[] = [
  { value: 'peacock', label: 'Peacock', hint: 'Peacock teal and blue' },
  { value: 'sapphire', label: 'Royal Sapphire', hint: 'Navy and sapphire blue' },
  { value: 'aubergine', label: 'Aubergine Velvet', hint: 'Aubergine and violet' },
  { value: 'onyx', label: 'Onyx and Rose', hint: 'Black and rose' },
];

/**
 * The three colours Ant Design has to be told in JavaScript, per accent and per theme.
 *
 * <p>
 * <strong>Duplicated from `style.css` rather than read back from it, and the duplication is the
 * same one `theme.ts` already accepts</strong> — a component that paints its own surfaces cannot
 * read a Tailwind class, and `ThemeConfig` is consumed during render. <strong>If a value here
 * disagrees with `style.css`, `style.css` is right.</strong>
 * </p>
 *
 * <p>
 * Only three, because only three are needed: `colorLink` and `colorInfo` take the brand, and every
 * selected-row surface in the product — the table, a `Select` option, a date-picker cell — takes
 * the wash. The rest of antd's palette is derived from them by its own algorithm.
 * </p>
 */
export interface AccentColours {
  /** `--color-primary`. */
  brand: string;
  /** `--color-primary-transparent`. Selected rows, soft badges. */
  wash: string;
  /** The wash one step down, for a selected row under the pointer. */
  washHover: string;
}

const colours: Record<Accent, Record<'light' | 'dark', AccentColours>> = {
  peacock: {
    light: { brand: '#0E7490', wash: '#E8F1F3', washHover: '#DAE9EC' },
    dark: { brand: '#4FB3C9', wash: '#17292E', washHover: '#1E363C' },
  },
  sapphire: {
    light: { brand: '#1D4ED8', wash: '#EBEFF8', washHover: '#DCE3F3' },
    dark: { brand: '#7C9CFF', wash: '#1D2338', washHover: '#252D48' },
  },
  aubergine: {
    light: { brand: '#7C3AED', wash: '#F0EBF8', washHover: '#E4DBF3' },
    dark: { brand: '#B49AF7', wash: '#261F38', washHover: '#302747' },
  },
  onyx: {
    light: { brand: '#BE185D', wash: '#F6EAF0', washHover: '#EEDAE4' },
    dark: { brand: '#F07AA8', wash: '#33202A', washHover: '#412833' },
  },
};

/** The three colours in force, for the theme given. */
export function resolveAccentColours(mode: 'light' | 'dark'): AccentColours {
  return colours[resolveAccent()][mode];
}

/**
 * Whether a value is an accent this product knows how to draw.
 *
 * <p>
 * Everything read back from storage or from the session goes through this, for the reason
 * `isTextScale` exists: a hand-edited storage entry or an accent since retired would otherwise put
 * a `data-accent` on the root element that no rule matches, and the page would fall through to the
 * default while the settings screen showed the stored name. Two answers to one question.
 * </p>
 */
export function isAccent(value: string | null | undefined): value is Accent {
  return typeof value === 'string' && Object.hasOwn(colours, value);
}

const USER_KEY = 'attirerent.accent';
const COMPANY_KEY = 'attirerent.accent.company';

const listeners = new Set<() => void>();

function readStored(key: string): Accent | null {
  try {
    const stored = localStorage.getItem(key);

    return isAccent(stored) ? stored : null;
  } catch {
    // Private mode, or storage disabled. Falling back is correct and silent — the product still
    // draws itself, in whichever accent below the one that could not be read.
    return null;
  }
}

let userAccent: Accent | null = readStored(USER_KEY);
let companyAccent: Accent | null = readStored(COMPANY_KEY);

function writeStored(key: string, value: Accent | null): void {
  try {
    if (value === null) {
      localStorage.removeItem(key);
    } else {
      localStorage.setItem(key, value);
    }
  } catch {
    // The choice still applies to this sitting; it simply does not survive a reload, which is a
    // better outcome than refusing to change the colour at all.
  }
}

function announce(): void {
  for (const listener of listeners) {
    listener();
  }
}

/** The accent in force: this person's choice, else their company's, else the default. */
export function resolveAccent(): Accent {
  return userAccent ?? companyAccent ?? defaultAccent;
}

/** What this person has chosen for this device, or null when they are following the company. */
export function userAccentChoice(): Accent | null {
  return userAccent;
}

/** What the company has chosen, or null when it has not. */
export function companyAccentChoice(): Accent | null {
  return companyAccent;
}

/**
 * Writes the accent onto the root element.
 *
 * <p>
 * One attribute rather than three custom properties: the trios live in `style.css` under
 * `[data-accent]`, so the stylesheet stays the single place a colour is written down. It also means
 * a screenshot or a stylesheet can tell which accent is in force by reading one name.
 * </p>
 */
export function applyAccent(accent: Accent = resolveAccent()): void {
  document.documentElement.setAttribute('data-accent', accent);
}

/**
 * Sets this device's override, or clears it with null so the company's choice applies again.
 *
 * Anything unrecognised clears rather than being stored, so a stale value from an older build
 * cannot strand somebody on an accent the product no longer draws.
 */
export function setUserAccent(accent: Accent | null): void {
  const next = isAccent(accent) ? accent : null;

  if (next === userAccent) {
    return;
  }

  userAccent = next;
  writeStored(USER_KEY, next);
  applyAccent();
  announce();
}

/**
 * Records the company's choice, from the session.
 *
 * Called whenever the signed-in user changes, including with null — a company that has cleared its
 * setting must fall back to the default rather than keep the last one this device happened to see.
 */
export function setCompanyAccent(accent: string | null): void {
  const next = isAccent(accent) ? accent : null;

  if (next === companyAccent) {
    return;
  }

  companyAccent = next;
  writeStored(COMPANY_KEY, next);
  applyAccent();
  announce();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
}

/**
 * Subscribes to the accent in force.
 *
 * <p>
 * `AppProviders` calls this, and that one call is what makes a change repaint Ant Design. Its
 * `colorLink`, `colorInfo` and the selected-row surfaces are resolved in JavaScript at render time,
 * so without a re-render the template's markup would change colour around a table and a set of
 * dropdowns that stayed on the old one. Same defect `useThemeMode` and `useTextScale` exist to
 * prevent, in the same place.
 * </p>
 */
export function useAccent(): Accent {
  return useSyncExternalStore(subscribe, resolveAccent, resolveAccent);
}

/*
  Applied at module load rather than only from an effect.

  `style.css` gives bare `:root` the peacock trio, so an unstamped page already renders the default
  correctly — which is why this cannot flash the way an unset theme would. What it *would* do
  without this line is show the default for one frame to somebody who chose another theme, then swap. The
  same reasoning as `applyTextScale`, and the same placement.
*/
applyAccent();
